import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { IntlProvider, useIntl } from "react-intl";
import { cloneElement, ReactNode, useEffect, useRef, useState } from "react";
import "rio-assist-widget/dist/rio-assist.js";
import ApplicationLayout from "@rio-cloud/rio-uikit/ApplicationLayout";
import { getColorScheme, listenForSchemeChange } from "@rio-cloud/rio-uikit/ColorScheme";

import { config } from "./config";
import { main } from "./configuration";
import { store } from "./configuration/setup/store";
import { handleLoginRedirect } from "./configuration/login/redirect";
import { ErrorBoundary } from "./components/errorTreatment/ErrorBoundary";
import { useDisplayMessages, useLocale } from "./configuration/lang/langSlice";
import { DEFAULT_LOCALE } from "./configuration/lang/lang";
import ErrorFallback from "./components/errorTreatment/ErrorFallback";
import { AppContext } from "./layout/AppContext";
import AppHeader from "./features/header/AppHeader";
import { UserRoleEnum } from "./enums/role";
import { checkRole } from "./services/roleService";
import useAssets from "./hooks/useAssets";
import { NotificationsContainer } from "@rio-cloud/rio-uikit";
import { getChassisListFromAssetIds } from "./utils/assetUtils";
import { applyRioAssistDarkMode } from "./utils/rioAssistDarkMode";
import { RioPageLoader } from "./utils/loading";
import "./utils/momentConfig";

import { Main } from "./pages";

import ControlTowerRoutes from "./pages/controlTower/routes";
import DealershipRoutes from "./pages/dealership/consultant/routes";
import FleetManagerRoutes from "./pages/fleetManager/routes";
import PartnershipRoutes from "./pages/partnership/routes";
import BackofficeRoutes from "./pages/backoffice/routes";

// Import sidebars
import ControlTowerLeftSidebar from "./pages/controlTower/LeftSidebar";
import DealershipLeftSidebar from "./pages/dealership/consultant/LeftSidebar";
import FleetManagerLeftSidebar from "./pages/fleetManager/LeftSidebar";
import PartnershipLeftSidebar from "./pages/partnership/LeftSidebar";
import { OccurrenceWithTimeSpent } from "./models/occurrence";

// Hooks
import { useFeatureFlag } from "./hooks/useFeatureFlag";
import { useApplicationLayoutScrollToTop } from "./hooks/useApplicationLayoutScrollToTop";

import "./index.css";

getColorScheme();
listenForSchemeChange();

declare global {
  interface Window {
    RioAssist?: {
      init: (options?: { rioToken?: string; [key: string]: unknown }) => void;
    };
  }
}

const setupRioAssistWidget = (() => {
  let lastToken: string | null = null;
  const homeUrl = config.homeRoute || "/";
  let cleanupFloatingOffset: (() => void) | null = null;
  let cleanupDarkMode: (() => void) | null = null;

  const triggerHeaderItem = (id: string) => {
    const target = document.getElementById(`rio-action-${id}`) as HTMLElement | null
      || document.querySelector<HTMLElement>(`[data-rio-action='${id}']`)
      || document.getElementById(id) as HTMLElement | null;
    if (!target) return;
    const clickable =
      target.querySelector<HTMLElement>(".ActionBarItemIcon") ||
      target.querySelector<HTMLElement>("[data-cy='userMenu-icon']") ||
      target.querySelector<HTMLElement>(".icon") ||
      target.querySelector<HTMLElement>("button, [role='button']");
    (clickable ?? target).click();
  };

  const headerActions = [
    {
      id: "service-info",
      iconUrl: "/assets/rio-assist/info.svg",
      ariaLabel: "Informacoes do servico",
      onClick: () => triggerHeaderItem("serviceInfo"),
    },
    {
      id: "notifications",
      iconUrl: "/assets/rio-assist/notification.svg",
      ariaLabel: "Notificacoes",
      onClick: () => triggerHeaderItem("platformNotifications"),
    },
    {
      id: "user-menu",
      iconUrl: "/assets/rio-assist/user.svg",
      ariaLabel: "Menu do usuario",
      onClick: () => triggerHeaderItem("userMenu"),
    },
  ];

  const applyWidgetHeaderConfig = (host: HTMLElement) => {
    if (homeUrl) {
      host.setAttribute("data-home-url", homeUrl);
      (host as any).homeUrl = homeUrl;
    }
    (host as any).headerActions = headerActions;
    host.removeEventListener("rioassist:header-action", handleHeaderAction as EventListener);
    host.addEventListener("rioassist:header-action", handleHeaderAction as EventListener);
    window.removeEventListener("rioassist:header-action", handleHeaderAction as EventListener);
    window.addEventListener("rioassist:header-action", handleHeaderAction as EventListener);
  };

  const handleHomeNavigation = (event: Event) => {
    if (!homeUrl) return;
    event.preventDefault();
    window.location.href = homeUrl;
  };

  const handleHeaderAction = (event: Event) => {
    const detail = (event as CustomEvent<{ index: number }>).detail;
    if (detail?.index == null) return;
    const action = headerActions[detail.index];
    if (!action?.onClick) return;
    event.preventDefault();
    elevatePopovers();
    action.onClick();
  };

  const elevatePopovers = () => {
    const className = "rio-assist-popover-elevate";
    document.body.classList.add(className);

    const monitor = () => {
      const hasOpen = Boolean(
        document.querySelector(".popover.show, .DialogModal.show, .DialogModal, .modal.show")
      );
      if (hasOpen) {
        window.setTimeout(monitor, 400);
      } else {
        document.body.classList.remove(className);
      }
    };

    window.setTimeout(monitor, 400);
  };

  const ensureRioAssistStyles = () => {
    const styleId = "rio-assist-host-style";
    if (document.getElementById(styleId)) return;
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      rio-assist-widget {
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 2147483646;
      }
      rio-assist-widget .floating-button,
      rio-assist-widget .panel,
      rio-assist-widget .fullscreen-shell {
        pointer-events: auto;
      }
    `;
    document.head.appendChild(style);
  };

  const styleHost = () => {
    const host = document.querySelector("rio-assist-widget") as HTMLElement | null;
    if (!host) return;
    host.style.position = "fixed";
    host.style.inset = "0";
    host.style.zIndex = "2147483646";
  };

  const applyFloatingOffset = (host: HTMLElement) => {
    cleanupFloatingOffset?.();
    const TOP_MARGIN = 50;
    const BUTTON_HEIGHT = 64;
    const MIN_BOTTOM = 12;

    const updateFloatingOffset = () => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const bottom = Math.max(MIN_BOTTOM, viewportHeight - TOP_MARGIN - BUTTON_HEIGHT);
      host.setAttribute("data-floating-offset", String(bottom));
    };

    updateFloatingOffset();
    window.addEventListener("resize", updateFloatingOffset);
    cleanupFloatingOffset = () => window.removeEventListener("resize", updateFloatingOffset);
  };

  const patchFloatingButtonDragLimit = (host: HTMLElement) => {
    const proto = Object.getPrototypeOf(host) as any;
    if (!proto || typeof proto.handleFloatingButtonPointerMove !== "function") return;
    if (proto.__rioAssistDragPatched) return;

    const HEADER_GAP = 50;
    const MIN_BOTTOM = 12;

    proto.handleFloatingButtonPointerMove = function (event: PointerEvent) {
      const dragState = (this as any).floatingButtonDragState;
      if (!dragState || dragState.pointerId !== event.pointerId) return;

      const { startY, startOffset, buttonHeight } = dragState;
      const deltaY = event.clientY - startY;
      const viewportHeight = window.innerHeight || (this as HTMLElement).getBoundingClientRect().height || 0;
      const maxBottom = Math.max(MIN_BOTTOM, viewportHeight - buttonHeight - HEADER_GAP);

      (this as any).floatingButtonOffset = (this as any).clamp(startOffset - deltaY, MIN_BOTTOM, maxBottom);
      (this as any).floatingButtonDragged =
        (this as any).floatingButtonDragged || Math.abs(deltaY) > 3;

      event.preventDefault();
    };

    proto.__rioAssistDragPatched = true;
  };

  const ensureHost = () => {
    let host = document.querySelector("rio-assist-widget") as HTMLElement | null;
    if (!host) {
      host = document.createElement("rio-assist-widget");
      document.body.appendChild(host);
    }
    return host;
  };

  const teardown = () => {
    const host = document.querySelector("rio-assist-widget");
    if (host?.parentElement) {
      host.parentElement.removeChild(host);
    }
    cleanupFloatingOffset?.();
    cleanupFloatingOffset = null;
    cleanupDarkMode?.();
    cleanupDarkMode = null;
    lastToken = null;
  };

  const initialize = (token: string) => {
    ensureRioAssistStyles();
    const host = ensureHost();
    if (host) {
      applyWidgetHeaderConfig(host);
      host.removeEventListener("rioassist:home", handleHomeNavigation as EventListener);
      host.addEventListener("rioassist:home", handleHomeNavigation as EventListener);
      patchFloatingButtonDragLimit(host);
      applyFloatingOffset(host);
      cleanupDarkMode?.();
      cleanupDarkMode = applyRioAssistDarkMode(host);
    }
    const boot = () => {
      window.RioAssist?.init({
        rioToken: token,
        accentColor: "#B23672",
      });
      requestAnimationFrame(() => {
        styleHost();
        if (host) {
          applyWidgetHeaderConfig(host);
        }
      });
    };

    if (window.RioAssist) {
      boot();
    } else {
      window.addEventListener("rio-assist-ready", boot, { once: true });
    }
  };

  return (token: string | null | undefined, isLoggedIn: boolean) => {
    const normalizedToken = token?.trim();
    const ready = Boolean(isLoggedIn) && normalizedToken && normalizedToken !== "NO_ACCESS_TOKEN_AVAILABLE";

    if (!ready) {
      teardown();
      return;
    }

    if (lastToken === normalizedToken) {
      return;
    }

    lastToken = normalizedToken;
    initialize(normalizedToken);
  };
})();

interface ISubNavigationHeaderProps {
  children: ReactNode;
  navItems: Array<{ key: string; value: string }>;
}

const SubNavigationHeader = ({ children, navItems }: ISubNavigationHeaderProps) => {
  return (
    <ApplicationLayout.Body className="uikitDemo">
      {children}
    </ApplicationLayout.Body>
  );
};

const AppLayout = ({ children }: { children: ReactNode }) => {
  const { getAssets, getGroups, allAssets } = useAssets();
  const [loadingAssets, setLoadingAssets] = useState(true);
  const [userRole, setUserRole] = useState<UserRoleEnum | null>(null);
  const location = useLocation();
  
  // Only scroll to top if the application layout is ready and not in backoffice
  const isApplicationLayoutReady =
    !loadingAssets && !location.pathname.startsWith("/backoffice");

  useApplicationLayoutScrollToTop(isApplicationLayoutReady);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const [assetIds, setAssetIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [chassisList, setChassisList] = useState<string[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [kanbanCurrentList, setKanbanCurrentList] = useState<OccurrenceWithTimeSpent[] | undefined>(undefined);
  const [kanbanFinalizedList, setKanbanFinalizedList] = useState<OccurrenceWithTimeSpent[] | undefined>(undefined);
  const [occurrenceDateFilters, setOccurrenceDateFilters] = useState<{
    startDateFilter: { start?: string; end?: string } | null;
    updateDateFilter: { start?: string; end?: string } | null;
  }>({ startDateFilter: null, updateDateFilter: null });

  // Feature flag
  const showBudget = useFeatureFlag("budget");
  const showBudgetManager = useFeatureFlag("budget.manager");
  const showBudgetConsultant = useFeatureFlag("budget.consultant");

  useEffect(() => {
    if (assetIds && assetIds.length > 0 && allAssets && allAssets.length > 0) {
      const newChassisList = getChassisListFromAssetIds(allAssets, assetIds);
      setChassisList(newChassisList);
    } else if (Array.isArray(assetIds) && assetIds.length === 0) {
      setChassisList([]);
    }
  }, [assetIds, allAssets]);

  useEffect(() => {
    const checkUserRole = async () => {
      try {
        if (location.pathname.startsWith('/control-tower')) {
          await checkRole(UserRoleEnum.TOWER);
          setUserRole(UserRoleEnum.TOWER);
        } else if (location.pathname.startsWith('/dealership')) {
          await checkRole(UserRoleEnum.CONSULTANT);
          setUserRole(UserRoleEnum.CONSULTANT);
        } else if (location.pathname.startsWith('/fleet-manager')) {
          await checkRole(UserRoleEnum.MANAGER);
          setUserRole(UserRoleEnum.MANAGER);
        } else if (location.pathname.startsWith('/partnership')) {
          await checkRole(UserRoleEnum.PARTNERSHIP);
          setUserRole(UserRoleEnum.PARTNERSHIP);
        } else if (location.pathname.startsWith('/backoffice')) {
          await checkRole(UserRoleEnum.TOWER);
          setUserRole(UserRoleEnum.TOWER);
        } else {
          await checkRole();
        }
      } catch (error) {
        // Role check will redirect if needed
      }
      await getAssets();
      await getGroups();
      setLoadingAssets(false);
    };

    checkUserRole();
  }, []);

  // Define navigation items based on current route
  const intl = useIntl();
  const getNavItems = () => {
    if (location.pathname.startsWith('/control-tower')) {
      const navItems = [
        { key: "control-tower/monitoring", value: intl.formatMessage({ id: "intl-msg:nav.monitoring" }) },
        { key: "control-tower/maintenance-monitoring", value: intl.formatMessage({ id: "intl-msg:nav.maintenanceMonitoring" }) },
        { key: "control-tower/appointments", value: intl.formatMessage({ id: "intl-msg:nav.appointments" }) },
      ];
      if (showBudget) navItems.push({ key: "control-tower/budgets-kanban", value: intl.formatMessage({ id: "intl-msg:nav.budgets" }) });
      navItems.push({ key: "control-tower/support", value: intl.formatMessage({ id: "intl-msg:nav.support" }) })
      return navItems;
    } else if (location.pathname.startsWith('/dealership') && !location.pathname.includes('/mechanic')) {
      const navItems = [
        { key: "dealership/monitoring", value: intl.formatMessage({ id: "intl-msg:nav.monitoring" }) },
        { key: "dealership/maintenance-control", value: intl.formatMessage({ id: "intl-msg:nav.maintenanceControl" }) },
        { key: "dealership/maintenance-monitoring", value: intl.formatMessage({ id: "intl-msg:nav.maintenanceMonitoring" }) },
        { key: "dealership/service-bay-control", value: intl.formatMessage({ id: "intl-msg:nav.serviceBayControl" }) },
        { key: "dealership/appointments", value: intl.formatMessage({ id: "intl-msg:nav.appointments" }) },
      ];
      if (showBudget && showBudgetConsultant) navItems.push({ key: "dealership/budgets-kanban", value: intl.formatMessage({ id: "intl-msg:nav.budgets" }) });
      navItems.push({ key: "dealership/support", value: intl.formatMessage({ id: "intl-msg:nav.support" }) })
      return navItems;
    } else if (location.pathname.startsWith('/dealership/mechanic')) {
      return [
        { key: "dealership/mechanic/requests", value: intl.formatMessage({ id: "intl-msg:nav.mechanicRequests" }) },
      ];
    } else if (location.pathname.startsWith('/fleet-manager')) {
      const navItems = [
        { key: "fleet-manager/preventive-maintenance", value: intl.formatMessage({ id: "intl-msg:nav.preventiveMaintenance" }) },
        { key: "fleet-manager/maintenance-monitoring", value: intl.formatMessage({ id: "intl-msg:nav.maintenanceMonitoring" }) },
        { key: "fleet-manager/appointments", value: intl.formatMessage({ id: "intl-msg:nav.appointments" }) },
      ];
      if (showBudget && showBudgetManager) navItems.push({ key: "fleet-manager/budgets-kanban", value: intl.formatMessage({ id: "intl-msg:nav.budgets" }) });

      return navItems;
    } else if (location.pathname.startsWith('/partnership')) {
      return [
        { key: "partnership/maintenance-monitoring", value: intl.formatMessage({ id: "intl-msg:nav.maintenanceMonitoring" }) },
      ];
    }
    return [];
  };

  // Get the appropriate sidebar component
  const getSidebarComponent = (children: ReactNode) => {
    if (location.pathname.startsWith('/control-tower')) {
      return (
        <ControlTowerLeftSidebar
          assetIds={assetIds}
          setAssetIds={setAssetIds}
          groupIds={groupIds}
          setGroupIds={setGroupIds}
          setChassisList={setChassisList}
          onSidebarStateChange={setIsSidebarOpen}
          occurrenceList={kanbanCurrentList}
          finalizedOccurrenceList={kanbanFinalizedList}
        >
          {children}
        </ControlTowerLeftSidebar>
      );
    } else if (location.pathname.startsWith('/dealership') && !location.pathname.includes('/mechanic')) {
      return (
        <DealershipLeftSidebar
          assetIds={assetIds}
          setAssetIds={setAssetIds}
          groupIds={groupIds}
          setGroupIds={setGroupIds}
          setChassisList={setChassisList}
          onSidebarStateChange={setIsSidebarOpen}
          occurrenceList={kanbanCurrentList}
          finalizedOccurrenceList={kanbanFinalizedList}
        >
          {children}
        </DealershipLeftSidebar>
      );
    } else if (location.pathname.startsWith('/fleet-manager')) {
      return (
        <FleetManagerLeftSidebar
          assetIds={assetIds}
          setAssetIds={setAssetIds}
          groupIds={groupIds}
          setGroupIds={setGroupIds}
          setChassisList={setChassisList}
          onSidebarStateChange={setIsSidebarOpen}
        >
          {children}
        </FleetManagerLeftSidebar>
      );
    } else if (location.pathname.startsWith('/partnership')) {
      return (
        <PartnershipLeftSidebar
          assetIds={assetIds}
          setAssetIds={setAssetIds}
          groupIds={groupIds}
          setGroupIds={setGroupIds}
          setChassisList={setChassisList}
        >
          {children}
        </PartnershipLeftSidebar>
      );
    }
    return null;
  };

  if (loadingAssets) {
    return <RioPageLoader />;
  }

  if (location.pathname.startsWith('/backoffice')) {
    return <>{children}</>;
  }

  return (
    <AppContext.Provider value={{ sidebarRef }}>
      <ApplicationLayout className="MyServiceLayout bg-lighter height-500">
        <ApplicationLayout.Header>
          <AppHeader navItems={getNavItems()} />
        </ApplicationLayout.Header>
        {getSidebarComponent(
          <SubNavigationHeader navItems={getNavItems()}>
            {cloneElement(children as React.ReactElement, {
              assetIds,
              setAssetIds,
              groupIds,
              chassisList,
              setChassisList,
              isSidebarOpen,
              onOccurrenceListsReady: (current: OccurrenceWithTimeSpent[], finalized: OccurrenceWithTimeSpent[]) => {
                setKanbanCurrentList(current);
                setKanbanFinalizedList(finalized);
              },
              initialDateFilters: occurrenceDateFilters,
              onDateFiltersChange: setOccurrenceDateFilters,
            })}
          </SubNavigationHeader>
        )}
        {/* <ApplicationLayout.Sidebar className="right" ref={sidebarRef} /> */}
      </ApplicationLayout>
    </AppContext.Provider>
  );
};

const App = () => {
  const userLocale = useLocale();
  const displayMessages = useDisplayMessages();

  if (!(displayMessages && userLocale)) {
    return null;
  }

  return (
    <IntlProvider
      defaultLocale={DEFAULT_LOCALE}
      key={userLocale}
      locale={userLocale}
      messages={displayMessages}
    >
      <ErrorBoundary fallback={<ErrorFallback />}>
        <BrowserRouter>
          <NotificationsContainer />
          <AppLayout>
            <RoutesWithProps />
          </AppLayout>
        </BrowserRouter>
      </ErrorBoundary>
    </IntlProvider>
  );
};

const RoutesWithProps = (props: any) => {
  return (
    <Routes>
      <Route path="/" index element={<Main {...props} />} />
      <Route path="/fleet-manager/*" index element={<FleetManagerRoutes {...props} persona="MANAGER" />} />
      <Route path="/dealership/*" index element={<DealershipRoutes {...props} persona="CONSULTANT" />} />
      <Route path="/control-tower/*" index element={<ControlTowerRoutes {...props} persona="TOWER" />} />
      <Route path="/partnership/*" index element={<PartnershipRoutes {...props} persona="PARTNERSHIP" />} />
      <Route path="/backoffice/*" index element={<BackofficeRoutes {...props} />} />
    </Routes>
  );
};

const renderApplication = () => {
  createRoot(document.getElementById("root") as HTMLElement).render(
    <Provider store={store}>
      <App />
    </Provider>
  );
};

const attachRioAssistToStore = () => {
  const state = store.getState();
  const manualToken = import.meta.env.VITE_RIO_ASSIST_TOKEN?.trim();
  const token = manualToken || state.tokenHandling?.accessToken;
  const isLoggedIn = Boolean(state.login?.userProfile) && state.login?.userSessionExpired === false;
  
  const userEmail = state.login?.userProfile?.email?.toLowerCase();
  const allowedEmails = (import.meta.env.VITE_RIO_ASSIST_ALLOWED_EMAILS ?? '')
    .split(',')
    .map((e: string) => e.trim().toLowerCase())
    .filter(Boolean);

  const isDev = import.meta.env.DEV;
  const isVwEmail = userEmail?.endsWith('@volkswagen.com.br') ?? false;
  const isRioAssistAllowed =
    isVwEmail ||
    allowedEmails.includes(userEmail ?? '') ||
    (isDev && allowedEmails.length === 0);

  if (isRioAssistAllowed) {
    setupRioAssistWidget(token, isLoggedIn);
  } else {
    setupRioAssistWidget(null, false); 
  }
};

attachRioAssistToStore();
store.subscribe(attachRioAssistToStore);

const isDev = import.meta.env.DEV;
const isProd = import.meta.env.PROD;
const isProdPreview = import.meta.env.VITE_PRODUCTION_PREVIEW;

if ((isDev && config.enableMockServer) || isProdPreview) {
  import("../mocks/browser")
    .then(({ startWorker }) => startWorker())
    .then(() => main(renderApplication));
}

if (window?.location.href.startsWith(config.login.redirectUri as string)) {
  handleLoginRedirect();
} else if (isProd && !isProdPreview) {
  main(renderApplication);
}
